/**
 * /yc — Y Combinator partner / founder invite landing page.
 *
 * Visitors with the URL `https://trubeaute.com/yc?code=YC-2026` see a
 * YC-branded welcome screen, drop their email, and get a magic link.
 * After they click the link, MagicLinkVerify routes them to /yc/claim
 * (via the backend's sanitized `next_path` field) which auto-redeems
 * the gift code for 6 months of Pro.
 *
 * Design choices:
 *   • Code is pulled from the URL `?code=` so the page is generic enough
 *     to host other VIP campaigns later (just point a different code
 *     at the same flow).
 *   • If the visitor is already signed in, we skip the magic link
 *     entirely and send them straight to /yc/claim — partners get the
 *     fastest possible path to Pro.
 *   • Friction: a single email field. No password, no Google OAuth
 *     toggle, no "create account" choice. Magic link is the only path
 *     because that's the path the backend's gift-code redemption
 *     piggybacks on for `next_path`.
 *
 * Security notes (defense in depth):
 *   • Backend caps the YC-2026 code at 3 unique redemptions total
 *     (see gift_codes._seed_yc_invite_code).
 *   • Backend sanitizes `next_path` on both insert and verify so it
 *     can only redirect to in-app paths (no `//evil.com` open-redirect).
 *   • Same email can't claim twice — `redeemed_by` array enforces
 *     per-user uniqueness atomically.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight, Mail, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default campaign code. Lets the page work as a bare `/yc` link too —
// any explicit `?code=...` in the URL takes precedence.
const DEFAULT_CODE = "YC-2026";

const YCAccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { i18n } = useTranslation();

  // Pull the gift code from the URL (?code=...). We pass it through
  // toUpperCase() because the backend stores codes upper-cased.
  const code = useMemo(() => {
    const raw = (params.get("code") || DEFAULT_CODE).trim();
    return raw.toUpperCase().slice(0, 40);
  }, [params]);

  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Already signed-in visitors skip the email step entirely — straight
  // to the claim page so their next click is "Pro is yours".
  useEffect(() => {
    if (user) {
      navigate(`/yc/claim?code=${encodeURIComponent(code)}`, { replace: true });
    }
  }, [user, code, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !cleaned.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/auth/magic-link/request`,
        {
          email: cleaned,
          origin: window.location.origin,
          locale: i18n.language || "en",
          // Post-auth landing — the verify endpoint will hand this
          // back to MagicLinkVerify which routes to /yc/claim.
          next_path: `/yc/claim?code=${encodeURIComponent(code)}`,
        },
        { withCredentials: true },
      );
      setSent(true);
      toast.success("Check your email — your invite is on the way.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "We couldn't send the email. Please try again in a moment.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12 bg-gradient-to-br from-[#FFF8F1] via-[#FFE9D4] to-[#FFC58A]"
      data-testid="yc-landing-root"
    >
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full bg-white/90 backdrop-blur-sm rounded-3xl soft-shadow-lg p-8 sm:p-10"
      >
        {/* YC orange-square mark. Inline SVG so we don't need to ship
            a separate logo asset. The "Y" glyph is set in Helvetica Bold
            to mirror YC's own brand mark. */}
        <div className="flex items-center gap-3 mb-6">
          <div
            className="h-10 w-10 rounded-md bg-[#FF6600] flex items-center justify-center text-white font-bold text-xl select-none"
            aria-hidden="true"
            data-testid="yc-logo"
          >
            Y
          </div>
          <span className="text-xs uppercase tracking-[0.22em] text-[#FF6600] font-semibold">
            For Y Combinator
          </span>
        </div>

        <h1 className="font-serif italic text-3xl sm:text-4xl text-[#2C2123] leading-tight mb-3">
          Welcome, YC.
        </h1>
        <p className="text-[#5C4B51] text-base leading-relaxed mb-6">
          Pro access on us — 6 months of unlimited routines, the AR
          mirror with your drag mom, the technique library, and every
          feature behind the paywall. Try it the way our paying users do.
        </p>

        <ul className="space-y-2.5 mb-7 text-sm text-[#3a2b2e]">
          {[
            "Unlimited personalised routines (face scan, recreate-a-look)",
            "Real-time AR mirror coaching with Trouvé, your drag mom",
            "Pro technique library — winged liner, smoky outer V, lifted brows",
            "Save snapshots, your Beauty Arsenal, the whole kit",
          ].map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-[#FF6600] shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {sent ? (
          <div
            className="rounded-2xl border border-[#FFD9B0] bg-[#FFF4E6] p-5 text-sm text-[#5C4B51]"
            data-testid="yc-sent-state"
          >
            <p className="font-medium text-[#2C2123] mb-1">
              Check your inbox.
            </p>
            <p>
              We sent a one-tap sign-in link to{" "}
              <span className="font-medium text-[#FF6600]">{email}</span>.
              Open it on this device and your Pro access is live — no
              password, no Stripe, no follow-up email.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="yc-form">
            <label
              htmlFor="yc-email"
              className="block text-xs uppercase tracking-[0.18em] text-[#5C4B51] font-medium"
            >
              Your YC email
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9B6A6C]" />
              <input
                id="yc-email"
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ycombinator.com"
                className="w-full pl-11 pr-4 py-3 rounded-full border border-[#EADDD7] bg-white text-[#2C2123] placeholder-[#A89798] focus:outline-none focus:ring-2 focus:ring-[#FF6600] focus:border-transparent transition-all"
                data-testid="yc-email-input"
                disabled={sending}
                required
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#FF6600] text-white font-medium hover:bg-[#E55A00] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              data-testid="yc-submit"
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending your invite…
                </>
              ) : (
                <>
                  Send me my Pro invite
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
            <p className="text-xs text-[#8A7D82] text-center pt-1">
              One link, no password, no credit card. Active for 6 months.
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default YCAccess;
